import React, { useEffect, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import type { StripeElementsOptions } from "@stripe/stripe-js";

import { getStripePromise } from "../../../helpers/stripeLoader";

interface StripeElementsProps {
  options?: StripeElementsOptions;
  children: React.ReactNode;
}

/**
 * Drop-in replacement for @stripe/react-stripe-js's <Elements> provider that
 * loads Stripe.js via getStripePromise (shared, retryable load) instead of a
 * module-scope loadStripe call.
 *
 * If Stripe.js can't be loaded (ad blocker, flaky mobile network) this THROWS
 * on the next render so the nearest error boundary handles it — on checkout
 * that's CheckoutErrorBoundary's in-place "reload to try again" message. The
 * bare <Elements> provider would instead render a dead card-form placeholder
 * forever, which is how failed loads went unnoticed. Reloading (or remounting)
 * retries the load because the loader clears its cache on failure.
 */
const StripeElements = ({ options, children }: StripeElementsProps) => {
  const [loadError, setLoadError] = useState<Error | null>(null);
  const stripePromise = useMemo(() => getStripePromise(), []);

  useEffect(() => {
    let isCurrent = true;
    stripePromise.then((stripe) => {
      if (isCurrent && !stripe) {
        // The underlying cause was already reported by getStripePromise; this
        // distinct error is what the boundary catches and logs with the
        // component stack of the payment UI that needed Stripe.
        setLoadError(new Error("Stripe.js failed to load"));
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [stripePromise]);

  if (loadError) throw loadError;

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
};

export default StripeElements;
